import { useCallback, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { bulkMergeReferenceDuplicates } from '../../services/firebase';
import type { WatchBrand } from '../../types';
import type {
  BulkMergeConfidence,
  BulkMergeReferenceDuplicatesResult,
  ClusterIdentityField,
} from '../../types/referenceDedup';
import { Badge } from '../common/Badge';
import { Button } from '../common/Button';
import { Modal } from '../common/Modal';

/**
 * Reference Cleanup — Phase 3 (bulk high-confidence auto-merge).
 *
 * Drives the `bulkMergeReferenceDuplicates` Cloud Function: for one brand, it
 * scans, picks the algorithmic canonical per cluster, and merges every cluster
 * in the chosen confidence band up to a safety cap. This is the bulk runner for
 * brands like IWC that have 600+ clusters (one-at-a-time Phase 2 merges don't
 * scale). See LEGACY_REF_DEDUP_PLAN.md §6.
 *
 * ⚠️ INERT until BOTH are done by James:
 *   (a) deploy `bulkMergeReferenceDuplicates`
 *       (`firebase deploy --only functions:bulkMergeReferenceDuplicates`); and
 *   (b) flip RTDB `configuration/admin/bulkMergeEnabled = true`.
 * Until (b) the callable throws `failed-precondition` and this panel shows a
 * banner telling James which flag to set (no need to dig through CF logs).
 *
 * Safety model (this is a DESTRUCTIVE bulk action — can merge HUNDREDS of
 * clusters in one click):
 *   - The brand is inherited from the page's most-recent scan (no local brand
 *     selector here — that used to drift out of sync with the scan filter).
 *   - Dry-run is OPTIONAL — "Run & merge" is always enabled when inputs are
 *     valid; the dry-run button is a preview-if-you-want, not a precondition.
 *   - The real run is gated behind a confirm modal.
 *   - The riskiest band (`high+medium+variant`) additionally requires a
 *     ≥10-char override reason before the run can fire.
 *   - During execution a spinner overlay blocks interaction and a beforeunload
 *     guard discourages navigating away mid-merge.
 */

const MAX_CLUSTERS_CAP = 1000;
const DEFAULT_MAX_CLUSTERS = 100;
const COLLAPSE_KEY = 'refCleanup.bulkMerge.collapsed';
/** Minimum trimmed length for the variant-override reason (mirrors the backend). */
const MIN_VARIANT_REASON = 10;

interface BulkMergePanelProps {
  /** Brands list — loaded once by the parent page, used to resolve the name. */
  brands: WatchBrand[];
  loadingBrands: boolean;
  /**
   * Brand the bulk run operates on — inherited from the page's most-recent
   * scan ('' / null = no scan yet, panel is disabled). No local selector: this
   * is the single source of truth so it can't drift from the scan filter.
   */
  brandId: string;
  /**
   * The identity field the most-recent scan keyed the inherited brand on
   * ('reference' | 'model'), from the scan's brandSummaries. Sent to the
   * callable as `expectedIdentityField` so the server aborts if its fresh
   * re-scan keys on a different field (stale deploy / brand edited mid-review) —
   * the root cause of the Seiko mass-merge. Undefined on older scan responses.
   */
  scanIdentityField?: ClusterIdentityField;
  /** Called after a successful REAL run so the parent can re-scan the brand. */
  onBulkMergeComplete: (brandId: string) => void;
}

export function BulkMergePanel({
  brands,
  loadingBrands,
  brandId,
  scanIdentityField,
  onBulkMergeComplete,
}: BulkMergePanelProps) {
  // Opt-in: collapsed by default so the destructive tool doesn't intimidate.
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    try {
      return localStorage.getItem(COLLAPSE_KEY) !== 'false';
    } catch {
      return true;
    }
  });
  const toggleCollapsed = useCallback(() => {
    setCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(COLLAPSE_KEY, String(next));
      } catch {
        /* ignore storage failures */
      }
      return next;
    });
  }, []);

  const [confidence, setConfidence] = useState<BulkMergeConfidence>('high');
  const [maxClusters, setMaxClusters] = useState<number>(DEFAULT_MAX_CLUSTERS);
  // Audit justification, required when confidence is `high+medium+variant`.
  const [variantOverrideReason, setVariantOverrideReason] = useState('');
  // Inline error surfaced under the textarea when the backend rejects the reason.
  const [variantReasonError, setVariantReasonError] = useState<string | null>(null);

  const [running, setRunning] = useState<'dry' | 'merge' | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [skippedExpanded, setSkippedExpanded] = useState(false);
  const [failedExpanded, setFailedExpanded] = useState(false);
  const [outcomesExpanded, setOutcomesExpanded] = useState(false);

  const [result, setResult] = useState<BulkMergeReferenceDuplicatesResult | null>(null);
  // True if the loaded result came from a real run (vs dry-run).
  const [resultIsReal, setResultIsReal] = useState(false);
  // Set when the callable reports the feature flag is off (shows banner, not toast).
  const [flagDisabled, setFlagDisabled] = useState(false);
  // Set when the callable's over-grouping sanity guard trips. Shows a prominent
  // warning (scanned count + identity field + worst cluster size) and reveals
  // the explicit override checkbox — the admin must consciously acknowledge.
  const [sanityError, setSanityError] = useState<{
    scanned: number;
    identityField: string;
    maxClusterSize: number;
    largeClusterCount: number;
  } | null>(null);
  // The conscious override. Sent as `acknowledgeSanity` only after a trip.
  const [acknowledgeSanity, setAcknowledgeSanity] = useState(false);
  // Set when the server's fresh re-scan keyed on a different field than the scan
  // the admin reviewed — never overridable (re-scan / redeploy and retry).
  const [divergenceError, setDivergenceError] = useState<{
    expected: string;
    actual: string;
  } | null>(null);

  // Reset the override + transient error banners whenever the run context
  // changes, so an acknowledgement can never silently carry into a new brand.
  useEffect(() => {
    setAcknowledgeSanity(false);
    setSanityError(null);
    setDivergenceError(null);
  }, [brandId, confidence, maxClusters]);

  const isVariantBand = confidence === 'high+medium+variant';
  const trimmedReason = variantOverrideReason.trim();
  // The variant band needs a sufficient reason; other bands have no extra gate.
  const variantReasonValid = !isVariantBand || trimmedReason.length >= MIN_VARIANT_REASON;
  // Inputs are valid to run when a brand is inherited and (if variant) reason is set.
  const canRun = !!brandId && variantReasonValid;

  const selectedBrandName = useMemo(() => {
    if (!brandId) return null;
    const b = brands.find((x) => x.id === brandId);
    return b ? b.displayName || b.name : brandId;
  }, [brands, brandId]);

  // Guard against accidental navigation while a real merge is in flight.
  useEffect(() => {
    if (running !== 'merge') return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [running]);

  const clampMaxClusters = (raw: number) => {
    if (Number.isNaN(raw)) return DEFAULT_MAX_CLUSTERS;
    return Math.max(1, Math.min(MAX_CLUSTERS_CAP, Math.round(raw)));
  };

  /** Detect the "bulk merge is not enabled" feature-flag precondition error. */
  const isFlagDisabledError = (err: unknown): boolean => {
    const code = (err as { code?: unknown }).code;
    const message = (err as { message?: unknown }).message;
    const msg = typeof message === 'string' ? message.toLowerCase() : '';
    const isPrecondition =
      typeof code === 'string' && code.includes('failed-precondition');
    return (
      (isPrecondition && msg.includes('not enabled')) ||
      msg.includes('bulk merge is not enabled')
    );
  };

  /**
   * Detect the backend's "variant override reason required" rejection. The
   * callable throws `HttpsError('invalid-argument', …, { code:
   * 'variant_override_reason_required' })` — surfaced inline under the textarea,
   * not as a toast.
   */
  const isVariantReasonError = (err: unknown): boolean => {
    const details = (err as { details?: unknown }).details;
    const detailCode =
      details && typeof details === 'object'
        ? (details as { code?: unknown }).code
        : undefined;
    if (detailCode === 'variant_override_reason_required') return true;
    const message = (err as { message?: unknown }).message;
    const msg = typeof message === 'string' ? message.toLowerCase() : '';
    return msg.includes('variant_override_reason_required');
  };

  const handleError = (err: unknown, phase: 'dry run' | 'merge') => {
    console.error(`Bulk reference merge ${phase} failed`, err);
    if (isFlagDisabledError(err)) {
      setFlagDisabled(true);
      return;
    }
    const details = (err as { details?: unknown }).details;
    const detail =
      details && typeof details === 'object'
        ? (details as Record<string, unknown>)
        : undefined;
    if (detail?.code === 'identity_field_divergence') {
      setDivergenceError({
        expected: String(detail.expected ?? scanIdentityField ?? 'reference'),
        actual: String(detail.actual ?? 'reference'),
      });
      return;
    }
    if (detail?.code === 'bulk_sanity_tripped') {
      setSanityError({
        scanned: Number(detail.scanned ?? 0),
        identityField: String(detail.identityField ?? scanIdentityField ?? 'reference'),
        maxClusterSize: Number(detail.maxClusterSize ?? 0),
        largeClusterCount: Number(detail.largeClusterCount ?? 0),
      });
      return;
    }
    if (isVariantReasonError(err)) {
      setVariantReasonError(
        `Bulk override reason is required (min ${MIN_VARIANT_REASON} characters).`,
      );
      return;
    }
    const code = (err as { code?: unknown }).code;
    const msg =
      typeof code === 'string' && code
        ? code
        : err instanceof Error
          ? err.message
          : String(err);
    toast.error(`Bulk ${phase} failed: ${msg}`);
  };

  const handleDryRun = useCallback(async () => {
    if (!brandId) {
      toast.error('Run a brand-filtered scan first.');
      return;
    }
    setRunning('dry');
    setFlagDisabled(false);
    setSanityError(null);
    setDivergenceError(null);
    setVariantReasonError(null);
    try {
      const res = await bulkMergeReferenceDuplicates({
        brandId,
        confidence,
        maxClusters,
        dryRun: true,
        ...(scanIdentityField ? { expectedIdentityField: scanIdentityField } : {}),
        ...(acknowledgeSanity ? { acknowledgeSanity: true } : {}),
        // Pass the reason if filled so the preview matches the real run.
        ...(trimmedReason ? { variantOverrideReason: trimmedReason } : {}),
      });
      setResult(res);
      setResultIsReal(false);
      setSkippedExpanded(false);
      setFailedExpanded(false);
      setOutcomesExpanded(false);
      toast.success(
        `Dry run complete — ${res.succeeded} of ${res.attempted} cluster${
          res.attempted === 1 ? '' : 's'
        } would merge.`,
      );
    } catch (err) {
      handleError(err, 'dry run');
    } finally {
      setRunning(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brandId, confidence, maxClusters, trimmedReason, scanIdentityField, acknowledgeSanity]);

  const handleMergeConfirmed = useCallback(async () => {
    setConfirmOpen(false);
    setRunning('merge');
    setFlagDisabled(false);
    setSanityError(null);
    setDivergenceError(null);
    setVariantReasonError(null);
    try {
      const res = await bulkMergeReferenceDuplicates({
        brandId,
        confidence,
        maxClusters,
        dryRun: false,
        ...(scanIdentityField ? { expectedIdentityField: scanIdentityField } : {}),
        ...(acknowledgeSanity ? { acknowledgeSanity: true } : {}),
        ...(isVariantBand ? { variantOverrideReason: trimmedReason } : {}),
      });
      setResult(res);
      setResultIsReal(true);
      setSkippedExpanded(false);
      setFailedExpanded(false);
      setOutcomesExpanded(false);
      toast.success(
        `Bulk merge complete — ${res.succeeded} cluster${
          res.succeeded === 1 ? '' : 's'
        } merged.`,
      );
      onBulkMergeComplete(brandId);
    } catch (err) {
      handleError(err, 'merge');
    } finally {
      setRunning(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brandId, confidence, maxClusters, isVariantBand, trimmedReason, scanIdentityField, acknowledgeSanity, onBulkMergeComplete]);

  // Aggregate downstream-rewrite + duplicate totals for the confirm modal copy.
  const dryTotals = useMemo(() => {
    if (!result) return { duplicates: 0, pointers: 0 };
    let duplicates = 0;
    let pointers = 0;
    for (const o of result.perClusterOutcomes) {
      duplicates += o.duplicatesDeleted;
      pointers +=
        o.rewrites.bookmarks +
        o.rewrites.wishlists +
        o.rewrites.curatedCollections +
        o.rewrites.submissionsIndex;
    }
    return { duplicates, pointers };
  }, [result]);

  const inputsDisabled = running !== null;

  return (
    <div className="rounded-lg border border-red-200 bg-white">
      {/* Header / collapse toggle */}
      <button
        type="button"
        onClick={toggleCollapsed}
        className="flex w-full items-center gap-2 rounded-t-lg px-4 py-3 text-left hover:bg-red-50"
      >
        <svg
          className={`h-4 w-4 text-gray-500 transition-transform ${collapsed ? '' : 'rotate-90'}`}
          viewBox="0 0 20 20"
          fill="currentColor"
          aria-hidden="true"
        >
          <path
            fillRule="evenodd"
            d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z"
            clipRule="evenodd"
          />
        </svg>
        <span className="text-sm font-semibold text-gray-900">
          Bulk merge (Phase 3)
        </span>
        <Badge variant="danger">destructive</Badge>
        <span className="ml-auto text-xs text-gray-400">
          {collapsed ? 'Show' : 'Hide'}
        </span>
      </button>

      {!collapsed && (
        <div className="relative space-y-4 border-t border-red-100 p-4">
          {/* Execution overlay — blocks interaction during a real merge / dry run. */}
          {running !== null && (
            <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-2 rounded-b-lg bg-white/70 backdrop-blur-sm">
              <svg
                className="h-8 w-8 animate-spin text-brand-600"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
              <span className="text-sm font-medium text-gray-700">
                {running === 'merge'
                  ? 'Merging clusters — do not navigate away…'
                  : 'Running dry run…'}
              </span>
            </div>
          )}

          <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800">
            <strong>⚠ This is a bulk destructive action.</strong> It can merge
            <strong> hundreds of clusters in a single click</strong>, deleting
            duplicate reference docs and rewriting downstream bookmarks, wishlists,
            curated collections and submission pointers. Use{' '}
            <strong>Dry run (preview)</strong> to inspect the plan first if you
            want — recommended for the variant-override band.
          </div>

          {/* Feature-flag-disabled banner (shown only when the callable says so). */}
          {flagDisabled && (
            <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900">
              <strong>Bulk merge is disabled.</strong> To enable, set RTDB{' '}
              <code className="rounded bg-amber-100 px-1 py-0.5 text-xs">
                configuration/admin/bulkMergeEnabled = true
              </code>
              . (The <code className="text-xs">bulkMergeReferenceDuplicates</code>{' '}
              Cloud Function must also be deployed.)
            </div>
          )}

          {/* Scan-divergence error — the server re-scanned and keyed on a
              different field than the scan you reviewed. NOT overridable. */}
          {divergenceError && (
            <div className="rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-900">
              <strong>Scan divergence — merge refused.</strong> You reviewed
              clustering by <code className="text-xs">{divergenceError.expected}</code>,
              but the server re-scanned and clustered by{' '}
              <code className="text-xs">{divergenceError.actual}</code>. This is how
              the Seiko mass-merge happened. Re-scan this brand, and make sure{' '}
              <code className="text-xs">bulkMergeReferenceDuplicates</code> is deployed
              from the latest build, before merging.
            </div>
          )}

          {/* Over-grouping sanity trip — far more / larger clusters than a
              healthy brand should produce. Overridable only via an explicit,
              conscious acknowledgement checkbox. */}
          {sanityError && (
            <div className="space-y-2 rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-900">
              <div>
                <strong>Sanity check failed — merge refused.</strong> The scan
                produced{' '}
                <span className="font-semibold">{sanityError.scanned}</span> candidate
                cluster{sanityError.scanned === 1 ? '' : 's'} keyed by{' '}
                <code className="text-xs">{sanityError.identityField}</code>
                {sanityError.maxClusterSize > 2 && (
                  <>
                    , with the largest holding{' '}
                    <span className="font-semibold">{sanityError.maxClusterSize}</span>{' '}
                    docs
                  </>
                )}
                . Legitimate duplicates almost always pair up — this looks like the
                identity field is over-grouping distinct watches. Verify this brand's
                Dedup identity setting and re-scan before proceeding.
              </div>
              <label className="inline-flex items-start gap-2 text-xs text-red-900">
                <input
                  type="checkbox"
                  checked={acknowledgeSanity}
                  onChange={(e) => setAcknowledgeSanity(e.target.checked)}
                  disabled={inputsDisabled}
                  className="mt-0.5 text-red-600 focus:ring-red-500"
                />
                <span>
                  I have reviewed the count and identity field above and want to
                  override the sanity guard for this run.
                </span>
              </label>
            </div>
          )}

          {/* Brand — inherited (read-only) from the page's most-recent scan. */}
          {brandId ? (
            <div className="text-sm text-gray-700">
              Brand:{' '}
              <span className="font-semibold text-gray-900">
                {loadingBrands ? brandId : (selectedBrandName ?? brandId)}
              </span>
              <span className="ml-2 text-xs text-gray-400">
                (from the most recent scan)
              </span>
            </div>
          ) : (
            <div className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-500">
              Run a brand-filtered scan to enable bulk merge.
            </div>
          )}

          {/* Confidence band */}
          <div>
            <span className="mb-1 block text-sm font-medium text-gray-700">
              Confidence band
            </span>
            <div className="flex flex-col gap-1.5 text-sm text-gray-700">
              <label className="inline-flex items-center gap-2">
                <input
                  type="radio"
                  name="bulkConfidence"
                  checked={confidence === 'high'}
                  onChange={() => setConfidence('high')}
                  disabled={inputsDisabled}
                  className="text-brand-600 focus:ring-brand-500"
                />
                <span>
                  High only <span className="text-gray-400">(safer)</span>
                </span>
              </label>
              <label className="inline-flex items-center gap-2">
                <input
                  type="radio"
                  name="bulkConfidence"
                  checked={confidence === 'high+medium'}
                  onChange={() => setConfidence('high+medium')}
                  disabled={inputsDisabled}
                  className="text-brand-600 focus:ring-brand-500"
                />
                <span>
                  High + medium{' '}
                  <span className="text-gray-400">(broader)</span>
                </span>
              </label>
              <label className="inline-flex items-center gap-2">
                <input
                  type="radio"
                  name="bulkConfidence"
                  checked={confidence === 'high+medium+variant'}
                  onChange={() => {
                    setConfidence('high+medium+variant');
                    setVariantReasonError(null);
                  }}
                  disabled={inputsDisabled}
                  className="text-amber-600 focus:ring-amber-500"
                />
                <span>
                  High + medium + variant{' '}
                  <span className="text-amber-700">(riskier — variant override)</span>
                </span>
              </label>
            </div>
          </div>

          {/* Variant-override warning + required audit reason. */}
          {isVariantBand && (
            <div className="space-y-2 rounded-md border border-amber-300 bg-amber-50 p-3">
              <p className="text-xs text-amber-900">
                <strong>⚠ Variant override.</strong> Variant clusters were
                excluded because at least one structured-field value (color,
                material, etc.) differed across docs. Bulk-overriding treats all
                of them as duplicates and merges using the highest-scored doc as
                canonical. Use only when you&apos;ve eyeballed enough samples to
                trust this for the brand.
              </p>
              <div>
                <label className="mb-1 block text-xs font-medium text-amber-900">
                  Bulk override reason (min {MIN_VARIANT_REASON} chars){' '}
                  <span className="text-red-600">*</span>
                </label>
                <textarea
                  value={variantOverrideReason}
                  onChange={(e) => {
                    setVariantOverrideReason(e.target.value);
                    if (variantReasonError) setVariantReasonError(null);
                  }}
                  disabled={inputsDisabled}
                  rows={2}
                  placeholder="e.g. Reviewed 30 IWC variant clusters — all are legacy color-in-model dupes, safe to merge."
                  className="w-full rounded-lg border border-amber-300 px-3 py-2 text-sm focus:border-amber-500 focus:ring-2 focus:ring-amber-500 disabled:opacity-50"
                />
                {variantReasonError ? (
                  <p className="mt-1 text-xs font-medium text-red-600">
                    {variantReasonError}
                  </p>
                ) : (
                  !variantReasonValid && (
                    <p className="mt-1 text-xs text-amber-700">
                      {MIN_VARIANT_REASON - trimmedReason.length} more character
                      {MIN_VARIANT_REASON - trimmedReason.length === 1 ? '' : 's'}{' '}
                      required.
                    </p>
                  )
                )}
              </div>
            </div>
          )}

          {/* maxClusters */}
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">
              Max clusters per run
              <span
                className="ml-1 cursor-help text-gray-400"
                title={`Safety cap. Each run merges at most this many clusters (hard cap ${MAX_CLUSTERS_CAP}). Start small to validate, then raise.`}
              >
                ⓘ
              </span>
            </label>
            <div className="flex items-center gap-3">
              <input
                type="range"
                min={1}
                max={MAX_CLUSTERS_CAP}
                step={1}
                value={maxClusters}
                onChange={(e) => setMaxClusters(clampMaxClusters(Number(e.target.value)))}
                disabled={inputsDisabled}
                className="h-2 w-full max-w-xs cursor-pointer disabled:opacity-50"
              />
              <input
                type="number"
                min={1}
                max={MAX_CLUSTERS_CAP}
                value={maxClusters}
                onChange={(e) => setMaxClusters(clampMaxClusters(Number(e.target.value)))}
                onBlur={(e) => setMaxClusters(clampMaxClusters(Number(e.target.value)))}
                disabled={inputsDisabled}
                className="w-24 rounded-lg border border-gray-300 px-2 py-1 text-sm focus:border-brand-500 focus:ring-2 focus:ring-brand-500 disabled:opacity-50"
              />
            </div>
            <p className="mt-1 text-xs text-gray-400">
              Capped at {MAX_CLUSTERS_CAP}. Lower = safer per-run blast radius.
            </p>
          </div>

          {/* Actions — dry-run is optional (a preview), not a precondition. */}
          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant="secondary"
              size="sm"
              loading={running === 'dry'}
              disabled={inputsDisabled || !canRun}
              onClick={handleDryRun}
            >
              Dry run (preview)
            </Button>
            <Button
              variant="danger"
              size="sm"
              loading={running === 'merge'}
              disabled={inputsDisabled || !canRun}
              onClick={() => setConfirmOpen(true)}
            >
              Run &amp; merge
            </Button>
            {!inputsDisabled && !brandId && (
              <span className="text-xs text-gray-500">
                Run a brand-filtered scan to enable bulk merge.
              </span>
            )}
            {!inputsDisabled && brandId && isVariantBand && !variantReasonValid && (
              <span className="text-xs text-amber-700">
                Enter an override reason (min {MIN_VARIANT_REASON} chars) to enable
                merging.
              </span>
            )}
          </div>

          {/* Result panel */}
          {result && (
            <div className="mt-2 space-y-3 rounded-lg border border-gray-200 bg-gray-50 p-3">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm font-semibold text-gray-900">
                  {resultIsReal ? 'Merge result' : 'Dry-run plan'}
                </span>
                <Badge variant={resultIsReal ? 'success' : 'info'}>
                  {resultIsReal ? 'written' : 'no writes'}
                </Badge>
                {selectedBrandName && (
                  <span className="text-xs text-gray-500">
                    {selectedBrandName} · {confidence} · cap {maxClusters}
                  </span>
                )}
              </div>

              <div className="grid grid-cols-2 gap-2 text-sm sm:grid-cols-5">
                <BulkStat label="Scanned" value={result.scanned} />
                <BulkStat label="Attempted" value={result.attempted} />
                <BulkStat
                  label={resultIsReal ? 'Succeeded' : 'Would succeed'}
                  value={result.succeeded}
                />
                <BulkStat
                  label="Skipped"
                  value={result.skipped.length}
                  muted={result.skipped.length > 0}
                />
                <BulkStat
                  label="Failed"
                  value={result.failed.length}
                  danger={result.failed.length > 0}
                />
              </div>

              {/* Skipped list */}
              {result.skipped.length > 0 && (
                <ExpandableList
                  title={`${result.skipped.length} skipped`}
                  tone="muted"
                  expanded={skippedExpanded}
                  onToggle={() => setSkippedExpanded((v) => !v)}
                  rows={result.skipped.map((s) => ({
                    key: s.identityKey,
                    reason: s.reason,
                  }))}
                />
              )}

              {/* Failed list */}
              {result.failed.length > 0 && (
                <ExpandableList
                  title={`${result.failed.length} failed`}
                  tone="danger"
                  expanded={failedExpanded}
                  onToggle={() => setFailedExpanded((v) => !v)}
                  rows={result.failed.map((f) => ({
                    key: f.identityKey,
                    reason: f.reason,
                  }))}
                />
              )}

              {/* Per-cluster outcomes (collapsed, scrollable) */}
              {result.perClusterOutcomes.length > 0 && (
                <div className="rounded-lg border border-gray-200 bg-white">
                  <button
                    type="button"
                    onClick={() => setOutcomesExpanded((v) => !v)}
                    className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm font-medium text-gray-700 hover:bg-gray-50"
                  >
                    <svg
                      className={`h-4 w-4 transition-transform ${outcomesExpanded ? 'rotate-90' : ''}`}
                      viewBox="0 0 20 20"
                      fill="currentColor"
                      aria-hidden="true"
                    >
                      <path
                        fillRule="evenodd"
                        d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z"
                        clipRule="evenodd"
                      />
                    </svg>
                    {result.perClusterOutcomes.length} per-cluster outcome
                    {result.perClusterOutcomes.length === 1 ? '' : 's'} — click to{' '}
                    {outcomesExpanded ? 'collapse' : 'expand'}
                  </button>
                  {outcomesExpanded && (
                    <div className="border-t border-gray-100">
                      <div className="max-h-80 overflow-auto">
                        <table className="w-full text-xs">
                          <thead className="sticky top-0 bg-gray-50 text-gray-500">
                            <tr>
                              <th className="px-3 py-1.5 text-left font-medium">
                                Identity key
                              </th>
                              <th className="px-3 py-1.5 text-left font-medium">
                                Canonical
                              </th>
                              <th className="px-3 py-1.5 text-right font-medium">
                                Dups{resultIsReal ? ' deleted' : ' (would del.)'}
                              </th>
                              <th className="px-3 py-1.5 text-right font-medium">
                                Rewrites
                              </th>
                              <th className="px-3 py-1.5 text-right font-medium">
                                Errors
                              </th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-100">
                            {result.perClusterOutcomes.map((o, i) => {
                              const rewriteTotal =
                                o.rewrites.bookmarks +
                                o.rewrites.wishlists +
                                o.rewrites.curatedCollections +
                                o.rewrites.submissionsIndex;
                              return (
                                <tr key={`${o.identityKey}/${i}`}>
                                  <td className="px-3 py-1.5 align-top font-mono text-gray-700">
                                    {o.identityKey}
                                  </td>
                                  <td className="px-3 py-1.5 align-top font-mono text-gray-700">
                                    {o.canonicalReferenceId}
                                  </td>
                                  <td className="px-3 py-1.5 text-right align-top text-gray-700">
                                    {o.duplicatesDeleted}
                                  </td>
                                  <td
                                    className="px-3 py-1.5 text-right align-top text-gray-700"
                                    title={`bookmarks ${o.rewrites.bookmarks}, wishlists ${o.rewrites.wishlists}, curated ${o.rewrites.curatedCollections}, submissions ${o.rewrites.submissionsIndex}`}
                                  >
                                    {rewriteTotal}
                                  </td>
                                  <td
                                    className={`px-3 py-1.5 text-right align-top ${o.errors.length > 0 ? 'font-semibold text-red-600' : 'text-gray-400'}`}
                                  >
                                    {o.errors.length}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {resultIsReal && (
                <p className="text-xs text-gray-500">
                  Cluster table above was re-scanned to reflect the new state.
                </p>
              )}
            </div>
          )}
        </div>
      )}

      {/* Confirm modal — the "are you sure" gate for the destructive real run. */}
      <Modal
        isOpen={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        title="Run bulk merge?"
      >
        <div>
          {/* Only cite preview counts when a fresh (non-real) dry-run is loaded. */}
          {result && !resultIsReal ? (
            <p className="text-sm text-gray-700">
              This will run{' '}
              <span className="font-semibold">{result.succeeded}</span> merge
              {result.succeeded === 1 ? '' : 's'} on{' '}
              <span className="font-semibold">{selectedBrandName ?? brandId}</span>,
              deleting up to{' '}
              <span className="font-semibold">{dryTotals.duplicates}</span> duplicate
              reference doc{dryTotals.duplicates === 1 ? '' : 's'} and rewriting up to{' '}
              <span className="font-semibold">{dryTotals.pointers}</span> downstream
              pointer{dryTotals.pointers === 1 ? '' : 's'} (confidence{' '}
              <span className="font-semibold">{confidence}</span>, cap {maxClusters}).
              This cannot be undone from the UI. Continue?
            </p>
          ) : (
            <p className="text-sm text-gray-700">
              This will bulk-merge up to{' '}
              <span className="font-semibold">{maxClusters}</span> cluster
              {maxClusters === 1 ? '' : 's'} on{' '}
              <span className="font-semibold">{selectedBrandName ?? brandId}</span>{' '}
              (confidence <span className="font-semibold">{confidence}</span>),
              deleting duplicate reference docs and rewriting downstream pointers.
              No dry-run preview is loaded — run one first if you want exact counts.
              This cannot be undone from the UI. Continue?
            </p>
          )}
          {scanIdentityField && (
            <p className="mt-2 text-xs text-gray-500">
              Clustering by{' '}
              <code className="text-xs">{scanIdentityField}</code> (from the scan you
              reviewed). The server will refuse the run if its fresh re-scan keys on a
              different field.
            </p>
          )}
          {acknowledgeSanity && (
            <p className="mt-2 rounded-md border border-red-300 bg-red-50 px-3 py-2 text-xs text-red-900">
              <strong>Sanity guard overridden.</strong> You are proceeding past the
              over-grouping check. Double-check the cluster count and identity field.
            </p>
          )}
          {isVariantBand && (
            <p className="mt-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900">
              <strong>Variant override active.</strong> Variant-excluded clusters
              will be merged on the highest-scored doc. Reason logged:{' '}
              <span className="italic">“{trimmedReason}”</span>
            </p>
          )}
          <div className="mt-4 flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setConfirmOpen(false)}>
              Cancel
            </Button>
            <Button variant="danger" onClick={() => void handleMergeConfirmed()}>
              Run &amp; merge
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

function BulkStat({
  label,
  value,
  danger,
  muted,
}: {
  label: string;
  value: number;
  danger?: boolean;
  muted?: boolean;
}) {
  return (
    <div className="rounded-lg border border-gray-200 bg-white px-3 py-2">
      <div className="text-xs text-gray-500">{label}</div>
      <div
        className={`text-lg font-semibold ${
          danger ? 'text-red-600' : muted ? 'text-amber-600' : 'text-gray-900'
        }`}
      >
        {value ?? 0}
      </div>
    </div>
  );
}

function ExpandableList({
  title,
  tone,
  expanded,
  onToggle,
  rows,
}: {
  title: string;
  tone: 'muted' | 'danger';
  expanded: boolean;
  onToggle: () => void;
  rows: Array<{ key: string; reason: string }>;
}) {
  const border = tone === 'danger' ? 'border-red-200' : 'border-gray-200';
  const text = tone === 'danger' ? 'text-red-700' : 'text-amber-700';
  const hover = tone === 'danger' ? 'hover:bg-red-50' : 'hover:bg-amber-50';
  return (
    <div className={`rounded-lg border ${border} bg-white`}>
      <button
        type="button"
        onClick={onToggle}
        className={`flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm font-medium ${text} ${hover}`}
      >
        <svg
          className={`h-4 w-4 transition-transform ${expanded ? 'rotate-90' : ''}`}
          viewBox="0 0 20 20"
          fill="currentColor"
          aria-hidden="true"
        >
          <path
            fillRule="evenodd"
            d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z"
            clipRule="evenodd"
          />
        </svg>
        {title} — click to {expanded ? 'collapse' : 'expand'}
      </button>
      {expanded && (
        <div className="border-t border-gray-100">
          <div className="max-h-72 overflow-auto">
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-gray-50 text-gray-500">
                <tr>
                  <th className="px-3 py-1.5 text-left font-medium">Identity key</th>
                  <th className="px-3 py-1.5 text-left font-medium">Reason</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {rows.map((r, i) => (
                  <tr key={`${r.key}/${i}`}>
                    <td className="px-3 py-1.5 align-top font-mono text-gray-700">
                      {r.key}
                    </td>
                    <td className="px-3 py-1.5 align-top text-gray-600">{r.reason}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
